import { LockOpenIcon } from '@heroicons/react/24/solid'
import { motion } from 'framer-motion'
import { useTheme } from 'next-themes'
import PriceBadge from '~/src/components/PriceBadge'
import LottiePlayer from '~/src/components/LottiePlayer'
import { pctFormatter } from '~/src/lib/helpers'
import { cn } from '~/src/lib/utils'

export const FIRE_STREAK_LOTTIE_URL =
  'https://lottie.host/8b33f345-6aa3-4a13-855e-67fc0b391ecc/dkz03Y4fzG.lottie'

// Match mobile thresholds from PriceDisplay.tsx
export const PRICE_DIFF_HIGH_THRESHOLD = 0.7 // 70%
const PRICE_DIFF_MEDIUM_THRESHOLD = 0.5 // 50%

// Get badge color based on price diff (matching mobile colors)
export const getPctColorClass = (
  priceDiff: number,
  isDarkMode: boolean
): string => {
  const priceDiffPct = priceDiff * 100
  if (priceDiffPct >= PRICE_DIFF_HIGH_THRESHOLD * 100) {
    return '#481FFF' // Purple for high savings
  }
  if (priceDiffPct >= PRICE_DIFF_MEDIUM_THRESHOLD * 100) {
    return isDarkMode ? '#3273d5' : '#0051cb' // Blue for medium savings
  }
  if (priceDiffPct > 0) {
    return isDarkMode ? '#0090F0' : '#4690ff' // Light blue for low savings
  }
  return '#f7f7f7' // fallback
}

interface PriceDropHiddenPriceLabelProps {
  label: string
  small?: boolean
  inline?: boolean
  large?: boolean
  buttonColorClass?: string
}

const PriceDropHiddenPriceLabel: React.FC<PriceDropHiddenPriceLabelProps> = ({
  label,
  small,
  inline,
  large,
  buttonColorClass,
}) => (
  <div
    className={cn(
      'flex items-start leading-none transition-colors',
      {
        'order-1': small || inline,
        'mr-2': inline,
      },
      buttonColorClass
    )}
  >
    <div
      className={cn('font-semibold leading-tight', {
        'text-xs md:text-sm': small || inline,
        'text-sm md:text-lg': large && !small && !inline,
        'text-sm md:text-base': !large && !small && !inline,
      })}
    >
      {label}
    </div>
  </div>
)

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  priceDiff: number
  price: string | undefined
  hidePrice?: boolean
  showSecondHandIcon?: boolean
  showBlackFridayIcon?: boolean
  showSaleIcon?: boolean
  showUnlockBadge?: boolean
  pctColorClass?: string
  buttonColorClass?: string
  isInView?: boolean
  small?: boolean
  showAsterisk?: boolean
  showWayDayIcon?: boolean
  large?: boolean
  inline?: boolean
  placeholder?: string
  autoTopMargin?: boolean
  showPrimeIcon?: boolean
  /** Shown in the price row when `hidePrice` is true (e.g. AMP Amazon listings). */
  hiddenPriceLabel?: string
}

const PriceDrop: React.FC<Props> = ({
  className,
  priceDiff,
  price,
  hidePrice,
  showSecondHandIcon,
  showBlackFridayIcon,
  showSaleIcon,
  showUnlockBadge,
  pctColorClass: pctColorClassProp,
  buttonColorClass,
  isInView,
  small,
  showAsterisk,
  showWayDayIcon,
  large,
  inline,
  placeholder,
  autoTopMargin,
  showPrimeIcon,
  hiddenPriceLabel,
}) => {
  const { resolvedTheme } = useTheme()
  const isDarkMode = resolvedTheme === 'dark'

  // Use dynamic color based on price diff (matching mobile)
  const dynamicBgColor = getPctColorClass(priceDiff, isDarkMode)
  const isHighSavings = priceDiff >= PRICE_DIFF_HIGH_THRESHOLD

  return (
    <div
      className={cn('flex flex-col gap-0.5', className, {
        'flex-row': small || inline,
        'mt-auto': autoTopMargin,
      })}
    >
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={isInView ? { y: 0, opacity: 1 } : { y: -50, opacity: 0 }}
        transition={{
          type: 'spring',
          damping: 12,
          stiffness: 250,
          mass: 0.7,
        }}
        className={cn('flex items-center space-x-1 leading-none', {
          'order-2': small || inline,
        })}
      >
        {price && priceDiff > 0 && (
          <div
            className={cn(
              'flex items-center justify-center whitespace-nowrap rounded-full px-2.5 font-semibold text-white',
              large ? 'h-7 text-sm' : 'h-6 text-xs',
              pctColorClassProp
            )}
            style={
              !pctColorClassProp ? { backgroundColor: dynamicBgColor } : {}
            }
          >
            <span className='uppercase'>{pctFormatter(priceDiff)} less</span>
            {isHighSavings && (
              <LottiePlayer
                src={FIRE_STREAK_LOTTIE_URL}
                autoplay
                loop
                style={{ width: 14, height: 14, marginLeft: 4, marginTop: -3 }}
              />
            )}
          </div>
        )}

        {/* {(!price || (price && priceDiff <= 0)) && (
          <div
            className={cn(
              'flex items-center justify-center whitespace-nowrap rounded-full px-2.5 font-semibold text-black/80',
              large ? 'h-7 text-sm' : 'h-6 text-xs',
              pctColorClassProp
            )}
            style={
              !pctColorClassProp ? { backgroundColor: dynamicBgColor } : {}
            }
          >
            Buy now
          </div>
        )} */}

        {showSecondHandIcon && (
          <PriceBadge
            className='bg-secondhand'
            size={large ? 'large' : undefined}
            rounded
          >
            Second hand
          </PriceBadge>
        )}

        {showBlackFridayIcon && (
          <PriceBadge className='bg-orange'>Cyber Monday</PriceBadge>
        )}

        {showPrimeIcon && (
          <PriceBadge
            className='bg-[#bb2a3e]'
            size={large ? 'large' : undefined}
            rounded
          >
            Prime Day
          </PriceBadge>
        )}

        {showWayDayIcon && (
          <PriceBadge
            className='bg-[#b058f6]'
            size={large ? 'large' : undefined}
            rounded
          >
            Way Day
          </PriceBadge>
        )}

        {showSaleIcon && (
          <PriceBadge className='bg-orange' size={large ? 'large' : undefined}>
            Sale
          </PriceBadge>
        )}

        {showUnlockBadge && (
          <PriceBadge
            className='bg-orange flex items-center space-x-1'
            size={large ? 'large' : undefined}
          >
            <div>Unlock</div>
            <LockOpenIcon height={15} className='mb-0.5 text-white' />
          </PriceBadge>
        )}
      </motion.div>

      {hidePrice && hiddenPriceLabel && (
        <PriceDropHiddenPriceLabel
          label={hiddenPriceLabel}
          small={small}
          inline={inline}
          large={large}
          buttonColorClass={buttonColorClass}
        />
      )}

      {!hidePrice && (price || placeholder) && (
        <div
          className={cn(
            'flex items-start leading-none transition-colors',
            {
              'order-1': small || inline,
              'mr-2': inline,
            },
            buttonColorClass
          )}
        >
          <div
            className={cn('text-base font-bold leading-none', {
              'md:text-2xl': !small && !placeholder,
              'text-xl md:text-2xl': large && !placeholder,
            })}
          >
            {price || placeholder}
          </div>
          {showAsterisk && <span>*</span>}
        </div>
      )}
    </div>
  )
}

export default PriceDrop
